import type {
  IceCandidate,
  SessionDescription,
  SignalPayload,
  Unsubscribe,
} from "@codeshare/signaling";

import { Channel } from "./channel";
import { type ConnectionStats, summarizeStats } from "./stats";

export type PeerState = "connecting" | "connected" | "disconnected" | "failed" | "closed";

export interface PeerOptions {
  selfId: string;
  peerId: string;
  /** Delivers a payload to this peer over signaling. */
  sendSignal: (payload: SignalPayload) => Promise<void>;
  rtcConfig?: RTCConfiguration;
  /** Injected for tests or non-browser runtimes (e.g. node-datachannel's polyfill). */
  RTCPeerConnection?: typeof RTCPeerConnection;
  /** Max wait for ICE gathering before sending a description anyway. */
  gatherTimeoutMs?: number;
  /** After "disconnected", wait this long for recovery before restarting ICE. */
  restartDelayMs?: number;
  /**
   * Give up ("failed") if not connected this long after starting, or after dropping: the other
   * side may be gone (a closed tab) or unreachable without a TURN server, and WebRTC alone
   * would keep trying forever.
   */
  connectTimeoutMs?: number;
}

export interface ChannelOptions {
  ordered?: boolean;
  maxRetransmits?: number;
}

function toIceCandidate(candidate: RTCIceCandidate): IceCandidate {
  const json = candidate.toJSON();
  return {
    candidate: json.candidate ?? "",
    sdpMid: json.sdpMid ?? null,
    sdpMLineIndex: json.sdpMLineIndex ?? null,
    usernameFragment: json.usernameFragment ?? null,
  };
}

/** The always-present control channel: negotiated out of band, so it needs no signaling. */
export const CONTROL_CHANNEL_ID = 0;
export const CONTROL_LABEL = "control";

/**
 * One WebRTC connection to one peer, using the "perfect negotiation" pattern: either side may
 * renegotiate at any time, and offer collisions resolve by role (the polite peer yields).
 *
 * To keep signaling traffic low (public relays rate-limit), descriptions are sent after ICE
 * gathering completes or a short timeout, carrying their candidates; only late candidates trickle.
 */
export class Peer {
  readonly kind = "webrtc" as const;
  readonly selfId: string;
  readonly peerId: string;
  /** Polite peers yield on offer collisions. Decided by id so both sides agree. */
  readonly polite: boolean;
  /** Control messages (hello, auth, ping). Opens with the connection. */
  readonly control = new Channel();

  readonly #options: Required<Omit<PeerOptions, "rtcConfig">> & { rtcConfig: RTCConfiguration };
  readonly #pc: RTCPeerConnection;
  readonly #stateListeners = new Set<(state: PeerState) => void>();
  readonly #channelListeners = new Set<(channel: Channel) => void>();
  #state: PeerState = "connecting";
  #makingOffer = false;
  #ignoreOffer = false;
  /** Bumped per local description; candidates trickle only after their description went out. */
  #descriptionGeneration = 0;
  #sentGeneration = -1;
  #pendingCandidates: RTCIceCandidateInit[] = [];
  #restartTimer: ReturnType<typeof setTimeout> | undefined;
  #connectTimer: ReturnType<typeof setTimeout> | undefined;
  #controlCreated = false;
  #answered = false;
  readonly #answeredListeners = new Set<() => void>();

  constructor(options: PeerOptions) {
    const RTCPeerConnectionImpl = options.RTCPeerConnection ?? globalThis.RTCPeerConnection;
    if (!RTCPeerConnectionImpl) throw new Error("WebRTC is not available");
    this.#options = {
      gatherTimeoutMs: 1_500,
      restartDelayMs: 3_000,
      connectTimeoutMs: 15_000,
      rtcConfig: {},
      RTCPeerConnection: RTCPeerConnectionImpl,
      ...options,
    };
    this.selfId = options.selfId;
    this.peerId = options.peerId;
    this.polite = options.selfId > options.peerId;
    this.#pc = new RTCPeerConnectionImpl(this.#options.rtcConfig);
    this.#wire();
    this.#startConnectTimer();
    // The impolite side starts: creating the first channel triggers the initial offer. The polite
    // side creates the negotiated control channel when that offer arrives (no second offer).
    if (!this.polite) {
      this.#createControl();
      // Browsers fire negotiationneeded for the first channel; not every implementation does.
      queueMicrotask(() => void this.#negotiate());
    }
  }

  get state(): PeerState {
    return this.#state;
  }

  /**
   * The other side has sent us a description (an offer or answer): it's really there. A peer that
   * never did is likely gone (a tab that has since closed), not unreachable.
   */
  get answered(): boolean {
    return this.#answered;
  }

  /** Called once, when the other side first answers. */
  onAnswered(listener: () => void): Unsubscribe {
    this.#answeredListeners.add(listener);
    return () => this.#answeredListeners.delete(listener);
  }

  /** Local and remote SDP, for DTLS fingerprints (connection-bound auth). */
  get localSdp(): string | undefined {
    return this.#pc.localDescription?.sdp;
  }

  get remoteSdp(): string | undefined {
    return this.#pc.remoteDescription?.sdp;
  }

  onStateChange(listener: (state: PeerState) => void): Unsubscribe {
    this.#stateListeners.add(listener);
    return () => this.#stateListeners.delete(listener);
  }

  /** Channels the remote side opened (not the control channel). */
  onChannel(listener: (channel: Channel) => void): Unsubscribe {
    this.#channelListeners.add(listener);
    return () => this.#channelListeners.delete(listener);
  }

  /** Opens a feature channel; `protocol` carries its negotiated version, e.g. "cs.doc/1". */
  openChannel(label: string, protocol: string, options: ChannelOptions = {}): Channel {
    return new Channel(this.#pc.createDataChannel(label, { protocol, ...options }));
  }

  /** Handles signaling from this peer (descriptions and candidates). */
  async handleSignal(payload: SignalPayload): Promise<void> {
    if (this.#state === "closed") return;
    if (payload.kind === "sdp") await this.#handleDescription(payload.description);
    else if (payload.kind === "ice") await this.#handleCandidate(payload.candidate);
  }

  /** A summary of the connection's current stats (selected path, RTT, channels). */
  async stats(): Promise<ConnectionStats> {
    return summarizeStats(await this.#pc.getStats());
  }

  /** Renegotiates ICE (e.g. after a network change). */
  restartIce(): void {
    if (this.#state === "closed") return;
    this.#pc.restartIce?.();
    void this.#negotiate();
  }

  close(): void {
    if (this.#state === "closed") return;
    clearTimeout(this.#restartTimer);
    clearTimeout(this.#connectTimer);
    this.control.close();
    this.#pc.close();
    this.#setState("closed");
  }

  #createControl(): void {
    if (this.#controlCreated) return;
    this.#controlCreated = true;
    this.control.attach(
      this.#pc.createDataChannel(CONTROL_LABEL, { negotiated: true, id: CONTROL_CHANNEL_ID }),
    );
  }

  #wire(): void {
    const pc = this.#pc;
    pc.onnegotiationneeded = () => void this.#negotiate();
    pc.onicecandidate = ({ candidate }) => {
      // Before the description is sent, candidates ride inside it. End-of-candidates (null) isn't
      // needed by the other side, so it isn't worth a signaling message.
      if (!candidate || this.#sentGeneration !== this.#descriptionGeneration) return;
      void this.#options
        .sendSignal({ kind: "ice", candidate: toIceCandidate(candidate) })
        .catch(() => {});
    };
    pc.onconnectionstatechange = () => this.#onConnectionState(pc.connectionState);
    pc.ondatachannel = ({ channel }) => {
      if (channel.label === CONTROL_LABEL && channel.id === CONTROL_CHANNEL_ID) return;
      const wrapped = new Channel(channel);
      for (const listener of this.#channelListeners) listener(wrapped);
    };
  }

  /** Makes and sends an offer, unless one is already in flight. */
  async #negotiate(): Promise<void> {
    const pc = this.#pc;
    if (this.#makingOffer || pc.signalingState !== "stable" || this.#state === "closed") return;
    try {
      this.#makingOffer = true;
      // Explicit createOffer (not implicit setLocalDescription()) for broader implementation support.
      const offer = await pc.createOffer();
      if (pc.signalingState !== "stable") return; // a remote offer arrived meanwhile
      await pc.setLocalDescription(offer);
      this.#descriptionGeneration++;
    } catch {
      return;
    } finally {
      this.#makingOffer = false;
    }
    await this.#sendLocalDescription();
  }

  async #sendLocalDescription(): Promise<void> {
    const generation = this.#descriptionGeneration;
    await this.#gathered();
    const description = this.#pc.localDescription;
    // A newer description (or a rollback after a collision) superseded this one.
    if (!description || generation !== this.#descriptionGeneration || this.#state === "closed")
      return;
    this.#sentGeneration = generation;
    await this.#options
      .sendSignal({
        kind: "sdp",
        description: { type: description.type, sdp: description.sdp },
      })
      .catch(() => {});
  }

  #gathered(): Promise<void> {
    const pc = this.#pc;
    if (pc.iceGatheringState === "complete") return Promise.resolve();
    return new Promise((resolve) => {
      const timer = setTimeout(done, this.#options.gatherTimeoutMs);
      function done() {
        clearTimeout(timer);
        pc.removeEventListener("icegatheringstatechange", check);
        resolve();
      }
      function check() {
        if (pc.iceGatheringState === "complete") done();
      }
      pc.addEventListener("icegatheringstatechange", check);
    });
  }

  async #handleDescription(description: SessionDescription): Promise<void> {
    if (!this.#answered) {
      this.#answered = true;
      for (const listener of this.#answeredListeners) listener();
    }
    const pc = this.#pc;
    const collision =
      description.type === "offer" && (this.#makingOffer || pc.signalingState !== "stable");
    this.#ignoreOffer = !this.polite && collision;
    if (this.#ignoreOffer) return;

    try {
      // Polite side of a collision: drop our pending offer and take theirs.
      if (collision && pc.signalingState === "have-local-offer") {
        await pc.setLocalDescription({ type: "rollback" });
        this.#descriptionGeneration++; // our offer is void; don't send it
      }
      await pc.setRemoteDescription(description as RTCSessionDescriptionInit);
    } catch {
      return; // stale or malformed; the next negotiation round recovers
    }
    if (this.polite) this.#createControl();
    await this.#flushCandidates();
    if (description.type === "offer") {
      try {
        await pc.setLocalDescription(await pc.createAnswer());
        this.#descriptionGeneration++;
      } catch {
        return;
      }
      await this.#sendLocalDescription();
    }
  }

  async #handleCandidate(candidate: RTCIceCandidateInit | null): Promise<void> {
    if (!candidate) return; // end-of-candidates: nothing to do
    if (!this.#pc.remoteDescription) {
      this.#pendingCandidates.push(candidate);
      return;
    }
    try {
      await this.#pc.addIceCandidate(candidate);
    } catch {
      // Expected for candidates of an offer we ignored; harmless otherwise.
    }
  }

  async #flushCandidates(): Promise<void> {
    for (const candidate of this.#pendingCandidates.splice(0))
      await this.#handleCandidate(candidate);
  }

  #onConnectionState(state: RTCPeerConnectionState): void {
    if (this.#state === "closed") return;
    clearTimeout(this.#restartTimer);
    switch (state) {
      case "connected":
        clearTimeout(this.#connectTimer);
        this.#setState("connected");
        break;
      case "disconnected":
        this.#setState("disconnected");
        // Often transient (network blip); if it doesn't recover, restart ICE, then give up.
        this.#restartTimer = setTimeout(() => this.restartIce(), this.#options.restartDelayMs);
        this.#startConnectTimer();
        break;
      case "failed":
        clearTimeout(this.#connectTimer);
        this.#setState("failed");
        break;
      case "closed":
        this.close();
        break;
    }
  }

  #startConnectTimer(): void {
    clearTimeout(this.#connectTimer);
    this.#connectTimer = setTimeout(() => {
      if (this.#state === "connecting" || this.#state === "disconnected") this.#setState("failed");
    }, this.#options.connectTimeoutMs);
  }

  #setState(state: PeerState): void {
    if (state === this.#state) return;
    this.#state = state;
    for (const listener of this.#stateListeners) listener(state);
  }
}
